/** Dependencies */
import angular from 'angular';
/** Styles */
import './MissingReceiptsAdmin.scss';
/** Templates */
import missingReceiptsAdminTemplate from './MissingReceiptsAdmin.template.html';
/** Dialogs */
import {
  CreateDepositAndReceiptDialogService,
  createDepositAndReceiptComponent
} from './dialogs/CreateDepositAndReceiptDialog';

/*******************************************************************************
 * csm.common.missingReceiptsAdmin
 *
 * Shared "Missing Receipts Admin" UI, moved here from CSMBooksWeb so
 * both the per-product Books admin screen (ASBWorks/BoosterFinance/PTAEZ, via
 * CSMBooksWeb's csm.books.missingReceiptsAdmin wrapper module — see
 * CSMBooksWeb/src/csmBooks/admin/MissingReceiptsAdmin/MissingReceiptsAdmin.js)
 * and CSMAdmin's Charge Detail "Fix Receipt" flow can share the same
 * client-side grid/dialog UI instead of each maintaining their own copy.
 *
 * This module intentionally does NOT register a `MissingReceiptsAdminService`.
 * The svc handlers stay separate per host app (books apps keep hitting their
 * existing books svc handler; CSMAdmin goes through AdminSvc)
 * even though both ultimately call into the same internal C# functions.
 * Whichever host app bootstraps this module is responsible for also
 * registering a `MissingReceiptsAdminService` with a matching interface
 * (getWebOrdersList / getDepositAndReceiptInfo / createDepositAndReceipt) —
 * see CSMBooksWeb's services/Services.js for the books-side implementation.
 *******************************************************************************/
export default angular
  .module('csm.common.missingReceiptsAdmin', [
    'ui.grid',
    'ui.grid.resizeColumns',
    'ui.grid.moveColumns',
    'ui.grid.selection'
  ])
  .component('missingReceiptsAdmin', {
    template: missingReceiptsAdminTemplate,
    controller: MissingReceiptsAdminController
  })
  .service(
    'CreateDepositAndReceiptDialog',
    CreateDepositAndReceiptDialogService
  )
  .component('createDepositAndReceipt', createDepositAndReceiptComponent).name;

/*******************************************************************************
 * MissingReceiptsAdminController */
/**
 * Base Controller
 *******************************************************************************/
/* @ngInject */ function MissingReceiptsAdminController(
  $scope,
  $injector,
  SERVER,
  Session
) {
  /**
   * Once all testing is done and we feel confident in this feature, we can remove the impersonation check and
   * just rely on the AccessControl to restrict access to this page. For now, since this is an admin tool that
   * could potentially be used to create receipts improperly, we'll keep it locked down to impersonation only.
   *
   * TODO: GR-2280 - Remove the check once we are ready to ship to our clients
   *
   * NOTE: this check (and the SERVER.BooksWebConstants.URL_WELCOME redirect target) is books-specific and
   * only applies when this component is hosted by a Books product. It is harmless anywhere else, since
   * `isProd` is only ever true on an asbworks.com hostname.
   */
  var isProd = window.location.hostname.includes('asbworks.com');
  if (!SERVER.currentUser.impersonate && isProd) {
    window.location.href = Session.getSessionURL(
      SERVER.BooksWebConstants.URL_WELCOME
    );
    return;
  }

  const $svcs = { $ctrl: this, $scope };

  $injector.invoke(initCtrl, null, Object.assign($svcs, { $svcs }));
}

/*******************************************************************************
 * initCtrl */
/**
 * Initializes the controller object, its handlers and the grid.
 *******************************************************************************/
/* @ngInject */ function initCtrl($ctrl, $injector, $svcs) {
  Object.assign($ctrl, {
    displaySourceApp: $injector.invoke(displaySourceApp, null, $svcs),
    displayPurchaser: $injector.invoke(displayPurchaser, null, $svcs),
    getWebOrders: $injector.invoke(getWebOrders, null, $svcs),
    initializeGrid: $injector.invoke(initializeGrid, null, $svcs),
    openCreateDepositAndReceiptDialog: $injector.invoke(
      openCreateDepositAndReceiptDialog,
      null,
      $svcs
    )
  });

  /**
   * Initialize the grid before loading the data, so that there is a structure in place for the data
   * to populate into once we get it back from the server. Otherwise, you will encounter binding errors
   * since the gridOptions object will not exist when the data is returned and Angular tries to bind it
   * to the grid.
   */
  $ctrl.initializeGrid();

  // HTML to display in our More Info Dialog. This explains the purpose of the page and gives instructions on how to use it.
  $ctrl.pageExplanation = `
    <p>
      This page displays Web Store transactions that were successfully processed but did not generate a
      corresponding receipts in the system. Only transactions older than three days are shown,
      allowing time for normal processing to complete before flagging an order as missing.
    </p>
    <p><strong>How to use this page:</strong></p>
    <ol>
      <li>Review the list of transactions with missing receipts displayed in the grid below.</li>
      <li>Click on a row to select the transaction you want to resolve/view.</li>
      <li>Click the <strong>View Selected Order</strong> button to open the order details.</li>
      <li>Review the deposit and receipt information, depending on what the system finds you may or may not already have a deposit.</li>
      <li>Whenever you are ready, click the <strong>Create</strong> button to generate a receipt for this transaction.
        <ul>
          <li>If a deposit already exists for this transaction, the receipt will be created and linked to the deposit.</li>
          <li>If no deposit exists, the system will create both a deposit and a receipt for the transaction.</li>
          <li>The receipt will be populated using the order details (purchaser, cart items, and payment amounts) shown in the grid.</li>
          <li>If at any point there is an error during this process, the receipt/deposit will not be created and an error message will be shown.</li>
        </ul>
      </li>
    </ol>
    <p><strong>Grid columns:</strong></p>
    <ul>
      <li><strong>Purchaser</strong> &ndash; The email address of the person who placed the order.</li>
      <li><strong>Cart</strong> &ndash; The items included in the order.</li>
      <li><strong>Subtotal / Tax / Fee Amount / Total</strong> &ndash; The financial breakdown of the order.</li>
      <li><strong>App</strong> &ndash; The storefront application where the transaction originated (e.g. Web Store).</li>
    </ul>
    <p class="text-muted">
      <em>If no transactions appear, there are currently no missing receipts older than three days or all missing receipts have been resolved.</em>
    </p>
  `;

  // load the initial web order data
  $ctrl.getWebOrders();
}

/*******************************************************************************
 * getWebOrders */
/**
 * On load of the page, grabs all of the web orders that are associated to this
 * site. We do not do any client side filtering, since the only data that exists
 * in the web orders table should be ones that are actually missing a receipt
 * within our system.
 *******************************************************************************/
/* @ngInject */ function getWebOrders(
  Alert,
  BusyScreen,
  $ctrl,
  $scope,
  MissingReceiptsAdminService
) {
  return function () {
    BusyScreen.setBusy(true);

    MissingReceiptsAdminService.getWebOrdersList()
      .then((webOrderList) => {
        $ctrl.gridOptions.data = webOrderList?.map((order) => ({
          date: order.date,
          webOrderID: order.id,
          source: order.source,
          purchaser: order.purchaser,
          purchaserEmail: order.email,
          cartItems: order.items,
          subtotal: order.subtotal,
          tax: order.tax,
          feeAmount: order.feeAmount,
          total: order.total
        }));
      })
      .catch((error) => {
        console.error(error);
        Alert.error(
          'Failed to gather transaction information.',
          'Please try again.'
        );
      })
      .finally(() => {
        // Update the bindings in the child components.
        $scope.$applyAsync();
        BusyScreen.setBusy(false);
      });
  };
}

/*******************************************************************************
 * initializeGrid */
/**
 * Build the grid/table based on the data we got from the initial request.
 *******************************************************************************/
/* @ngInject */ function initializeGrid($ctrl, $scope, uiGridConstants) {
  // Template for displaying cart items similar to ReceiptInfo memoTemplate
  const cartTemplate = `
    <div ng-repeat="item in row.entity.cartItems" class="cart-item">
      <div class="ui-grid-cell-contents"><strong>{{item.memo}}</strong></div>
      <div ng-if="item.subItems && item.subItems.length" ng-repeat="subItem in item.subItems" class="sub-item-tab-space ui-grid-cell-contents">
        {{subItem.memo}}
        <ul ng-if="subItem.itemOptions && subItem.itemOptions.length">
          <li ng-repeat="subItemOption in subItem.itemOptions" ng-if="subItem.itemID === subItemOption.itemID">
            {{subItemOption.optionName}}: {{subItemOption.value}}
          </li>
        </ul>
      </div>
      <ul class="ui-grid-cell-contents" ng-if="!(item.subItems && item.subItems.length) && item.itemOptions && item.itemOptions.length">
        <li ng-repeat="itemOption in item.itemOptions">
          {{itemOption.optionName}}: {{itemOption.value}}
        </li>
      </ul>
    </div>
  `;

  const columnDefs = [
    {
      field: 'date',
      displayName: 'Transaction Date',
      width: 150,
      cellFilter: 'date: "MM-dd-yyyy"'
    },
    {
      field: 'purchaser',
      displayName: 'Purchaser',
      width: 250,
      cellTemplate: `
        <div class="ui-grid-cell-contents">
          {{ grid.appScope.$ctrl.displayPurchaser(row.entity.purchaser, row.entity.purchaserEmail) }}
        </div>
      `
    },
    {
      field: 'cartItems',
      displayName: 'Cart',
      width: 350,
      cellTemplate: cartTemplate,
      enableSorting: false
    },
    {
      field: 'subtotal',
      displayName: 'Subtotal',
      minWidth: 80,
      cellFilter: 'currency',
      cellClass: 'text-right'
    },
    {
      field: 'tax',
      displayName: 'Tax',
      minWidth: 80,
      cellFilter: 'currency',
      cellClass: 'text-right'
    },
    {
      field: 'feeAmount',
      displayName: 'Fee Amount',
      minWidth: 80,
      cellFilter: 'currency',
      cellClass: 'text-right'
    },
    {
      field: 'total',
      displayName: 'Total',
      minWidth: 80,
      cellFilter: 'currency',
      cellClass: 'text-right'
    },
    {
      field: 'source',
      displayName: 'App',
      minWidth: 120,
      cellTemplate: `
        <div class="ui-grid-cell-contents">
          {{ grid.appScope.$ctrl.displaySourceApp(row.entity.source) }}
        </div>
      `
    }
  ];

  return function () {
    $ctrl.gridOptions = {
      minRowsToShow: 5,
      enableFiltering: true,
      enableSorting: true,
      enableRowSelection: true,
      enableSelectAll: false,
      multiSelect: false,
      enableRowHeaderSelection: false,
      enableVirtualScrolling: false,
      enableHorizontalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
      enableVerticalScrollbar: uiGridConstants.scrollbars.NEVER,
      columnDefs,
      data: [],
      onRegisterApi: function (gridApi) {
        $ctrl.gridOptions.api = gridApi;

        /**
         * ui-grid intercepts wheel events on its render container and calls preventDefault()
         * then manually sets scrollTop using row-count × fixed-rowHeight math. Since our rows
         * use height: auto, its calculated canvas height is wrong and the viewport snaps back.
         *
         * Fix: attach a capture-phase listener on the outermost grid element — above all
         * ui-grid internals. Capture fires top-down, so this runs before ui-grid's listener
         * on any descendant element. We manually scroll the viewport by the correct delta,
         * then call stopImmediatePropagation() + preventDefault() so ui-grid never sees the
         * event and cannot apply its incorrect scroll math.
         */
        var gridElement = gridApi.grid.element[0];
        gridElement.addEventListener(
          'wheel',
          function (e) {
            var viewport = gridElement.querySelector(
              '.ui-grid-render-container-body .ui-grid-viewport'
            );
            if (viewport) {
              var delta =
                e.deltaMode === 1
                  ? e.deltaY * 30
                  : e.deltaMode === 2
                    ? e.deltaY * viewport.clientHeight
                    : e.deltaY;
              viewport.scrollTop += delta;
            }
            e.stopImmediatePropagation();
            e.preventDefault();
          },
          { capture: true, passive: false }
        );

        // Handle row selection
        gridApi.selection.on.rowSelectionChanged($scope, function (row) {
          $ctrl.selectedRow = row.entity;
        });
      }
    };
  };
}

/*******************************************************************************
 * displaySourceApp */
/**
 * Using the source value of the order, determine which app the transaction
 * occurred.
 *
 * @param  source  Receipt source constant of the order.
 *******************************************************************************/
/* @ngInject */ function displaySourceApp(SERVER) {
  return function (source) {
    switch (source) {
      case SERVER.BooksDBConstants.RS_COMMUNITY_WEBSTORE:
        return 'Community Web Store';
      case SERVER.BooksDBConstants.RS_WEBSTORE:
        return 'Web Store';
      default:
        return 'Unknown';
    }
  };
}

/*******************************************************************************
 * displayPurchaser */
/**
 * Returns a display string for the purchaser column. Shows "Name (email)" when
 * both are present, falls back to whichever is available.
 *******************************************************************************/
function displayPurchaser() {
  return function (purchaser, purchaserEmail) {
    if (!purchaser) return purchaserEmail || '';
    if (!purchaserEmail) return purchaser;
    return `${purchaser} (${purchaserEmail})`;
  };
}

/*******************************************************************************
 * openCreateDepositAndReceiptDialog */
/**
 * Request the deposit and receipt information for the selected web order,
 * then open the CreateDepositAndReceiptDialog with that information. For a
 * POS sale whose card payments don't cover its minimum payment, the dialog
 * warns the admin before showing anything else.
 *******************************************************************************/
/* @ngInject */ function openCreateDepositAndReceiptDialog(
  Alert,
  BusyScreen,
  $ctrl,
  CreateDepositAndReceiptDialog
) {
  return function () {
    const { webOrderID } = $ctrl.selectedRow;

    BusyScreen.setBusy(true);

    /**
     * Once we have the deposit and receipt info, open the dialog. The dialog resolves only when $ctrl.close() is
     * called (i.e. after a successful save), and rejects when dismissed (close button, ESC, backdrop). This
     * is in place because we only want to refresh the web orders list after a successful creation of a deposit
     * and receipt, and not on dismissal of the dialog. Why? We don't really want to refetch data if the user intentionally
     * or accidentally dismisses the dialog, since in theory no data changed. Prevents further service calls and the user
     * does not need to wait for data fetch whenever they close a modal, intentionally or accidentally.
     *
     * The empty rejection handler suppresses the unhandled-rejection console error on dismiss.
     */
    CreateDepositAndReceiptDialog.loadOrderInfo(webOrderID)
      .then((depositAndReceiptInfo) => {
        CreateDepositAndReceiptDialog.show(
          $ctrl.selectedRow,
          depositAndReceiptInfo
        ).then(
          () => {
            $ctrl.getWebOrders();
          },
          () => {}
        );
      })
      .catch((error) => {
        console.error('Error fetching deposit and receipt info: ', error);
        Alert.error(
          error.parsedResponse
            ? error.parsedResponse.message()
            : 'Failed to load order information.',
          'Missing Receipts Admin'
        );
      })
      .finally(() => {
        BusyScreen.setBusy(false);
      });
  };
}
